package com.vidaensupalabra.vsp.ventanas

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

import com.vidaensupalabra.vsp.datos.BibleUtils
import com.vidaensupalabra.vsp.datos.ArdeEntity
import com.vidaensupalabra.vsp.datos.ArdeRepository
import com.vidaensupalabra.vsp.datos.BibleRepository
import com.vidaensupalabra.vsp.ui.theme.VspBase
import com.vidaensupalabra.vsp.ui.theme.VspMarco
import com.vidaensupalabra.vsp.ui.theme.VspMarcoTransparente50
import com.vidaensupalabra.vsp.ui.theme.White
import com.vidaensupalabra.vsp.utils.*

private const val FONT_SIZE_KEY = "bible_font_size"
private const val DEBOUNCE_DELAY = 500L

private fun aplicarFormatoDevocional(formato: String, onUpdate: (String) -> Unit) {
    val element = window.document.getElementById("devocional-content-area")
    element?.asDynamic()?.focus()
    window.document.execCommand(formato, false, "")
    
    // Forzar actualización del estado de Compose después de aplicar el comando nativo
    window.setTimeout({
        val newHtml = element?.asDynamic()?.innerHTML as? String ?: ""
        onUpdate(newHtml)
    }, 50)
}

private val REGEX_BOLD_1 = Regex("<b[^>]*>([\\s\\S]*?)</b>", RegexOption.IGNORE_CASE)
private val REGEX_BOLD_2 = Regex("<strong[^>]*>([\\s\\S]*?)</strong>", RegexOption.IGNORE_CASE)
private val REGEX_ITALIC_1 = Regex("<i[^>]*>([\\s\\S]*?)</i>", RegexOption.IGNORE_CASE)
private val REGEX_ITALIC_2 = Regex("<em[^>]*>([\\s\\S]*?)</em>", RegexOption.IGNORE_CASE)
private val REGEX_LI = Regex("<li[^>]*>([\\s\\S]*?)</li>", RegexOption.IGNORE_CASE)
private val REGEX_BR = Regex("<br\\s*/?>", RegexOption.IGNORE_CASE)
private val REGEX_DIV = Regex("</div>", RegexOption.IGNORE_CASE)
private val REGEX_P = Regex("</p>", RegexOption.IGNORE_CASE)
private val REGEX_STRIP_ALL = Regex("<[^>]+>")
private val REGEX_MULTI_NEWLINES = Regex("\n{3,}")

private fun htmlToWhatsApp(html: String): String {
    var text = html
    text = text.replace(REGEX_BOLD_1, "*$1*")
    text = text.replace(REGEX_BOLD_2, "*$1*")
    text = text.replace(REGEX_ITALIC_1, "_$1_")
    text = text.replace(REGEX_ITALIC_2, "_$1_")
    text = text.replace(Regex("<ul[^>]*>", RegexOption.IGNORE_CASE), "")
    text = text.replace(Regex("</ul>", RegexOption.IGNORE_CASE), "")
    text = text.replace(REGEX_LI, "- $1\n")
    text = text.replace(REGEX_BR, "\n")
    text = text.replace(REGEX_DIV, "\n")
    text = text.replace(REGEX_P, "\n")
    text = text.replace(REGEX_STRIP_ALL, "")
    text = text.replace("&nbsp;", " ")
    text = text.replace("&lt;", "<")
    text = text.replace("&gt;", ">")
    text = text.replace("&amp;", "&")
    text = text.replace(REGEX_MULTI_NEWLINES, "\n\n")
    return text.trim()
}

@Composable
fun DevocionalScreen(arde: ArdeEntity?, onSave: (ArdeEntity) -> Unit, onClose: () -> Unit) {
    LaunchedEffect(Unit) {
        ArdeRepository.loadDataIfNeeded()
    }

    if (arde == null) {
        if (!ArdeRepository.isDataLoaded) {
            // Estado de carga temporal mientras se descarga el CSV
            Div(attrs = {
                style {
                    width(100.percent); height(100.vh); backgroundColor(VspBase)
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
                    color(VspMarco); fontSize(18.px); fontWeight("bold"); fontFamily("sans-serif")
                }
            }) { Text("Cargando ARDE...") }
        } else {
            EmptyDevocionalState(onClose)
        }
        return
    }

    DevocionalActiveState(arde, onSave, onClose)
}

@Composable
private fun EmptyDevocionalState(onClose: () -> Unit) {
    Div(attrs = {
        style {
            width(100.percent)
            height(100.vh)
            backgroundColor(VspBase)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            fontFamily("sans-serif")
        }
    }) {
        P(attrs = { style { color(White); fontSize(18.px) } }) {
            Text("No hay datos ARDE disponibles. Seleccione una fecha.")
        }
        Button(attrs = {
            onClick { onClose() }
            style {
                marginTop(16.px)
                padding(10.px, 24.px)
                backgroundColor(VspMarco)
                color(White)
                border(0.px)
                borderRadius(8.px)
                cursor("pointer")
                fontSize(16.px)
            }
        }) { Text("Volver") }
    }
}

@Composable
private fun DevocionalActiveState(arde: ArdeEntity, onSave: (ArdeEntity) -> Unit, onClose: () -> Unit) {
    val localStorageKey = "arde_${arde.reference}"
    val coroutineScope = rememberCoroutineScope()
    var currentUser by remember { mutableStateOf<FirebaseUser?>(null) }
    var isSyncing by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        if (window.asDynamic().addAuthChangeListener != undefined) {
            window.asDynamic().addAuthChangeListener { user: FirebaseUser? ->
                currentUser = user
            }
        }
    }
    
    var devocionalText by remember(arde.reference) {
        mutableStateOf(getFromLocalStorage(localStorageKey) ?: arde.devocional)
    }

    LaunchedEffect(arde.reference, currentUser) {
        if (currentUser != null) {
            isSyncing = true
            val remoteText = ArdeRepository.getDevocional(currentUser!!.uid, arde.reference)
            if (remoteText != null && remoteText.isNotBlank() && remoteText != devocionalText) {
                devocionalText = remoteText
                saveToLocalStorage(localStorageKey, remoteText)
            }
            isSyncing = false
        }
    }
    
    var hasModified by remember { mutableStateOf(false) }
    var feedbackMessage by remember { mutableStateOf("") }
    var feedbackTrigger by remember { mutableStateOf(0) }

    LaunchedEffect(feedbackTrigger) {
        if (feedbackMessage.isNotEmpty()) {
            delay(2500)
            feedbackMessage = ""
        }
    }

    val showFeedback: (String) -> Unit = { msg ->
        feedbackMessage = msg
        feedbackTrigger++
    }

    val currentDevocionalText by rememberUpdatedState(devocionalText)
    val currentHasModified by rememberUpdatedState(hasModified)
    
    DisposableEffect(localStorageKey) {
        onDispose {
            if (currentHasModified) {
                saveToLocalStorage(localStorageKey, currentDevocionalText)
            }
        }
    }

    LaunchedEffect(devocionalText) {
        if (hasModified && !isSyncing) {
            delay(DEBOUNCE_DELAY)
            saveToLocalStorage(localStorageKey, devocionalText)
            if (currentUser != null) {
                ArdeRepository.saveDevocional(currentUser!!.uid, arde.reference, devocionalText)
            }
        }
    }

    // Contenedor principal scrollable
    Div(attrs = {
        style {
            width(100.percent)
            minHeight(100.vh)
            backgroundColor(VspBase)
            fontFamily("sans-serif")
            position(Position.Relative)
        }
    }) {
        Div(attrs = {
            style {
                padding(16.px)
                maxWidth(800.px)
                property("margin", "0px auto") // Centrar en pantallas grandes
                boxSizing("border-box")
            }
        }) {
            DevocionalHeader(arde.reference)
            
            Div(attrs = { style { height(8.px) } })

            DevocionalEditor(
                value = devocionalText,
                onValueChange = { 
                    devocionalText = it
                    hasModified = true 
                }
            )
            
            Div(attrs = { style { height(16.px) } })

            DevocionalActions(
                onSave = {
                    onSave(arde.copy(devocional = devocionalText))
                    saveToLocalStorage(localStorageKey, devocionalText)
                    if (currentUser != null) {
                        coroutineScope.launch {
                            ArdeRepository.saveDevocional(currentUser!!.uid, arde.reference, devocionalText)
                            showFeedback("¡ARDE guardado y sincronizado en la nube!")
                        }
                    } else {
                        showFeedback("¡ARDE guardado localmente! (Inicia sesión para sincronizar)")
                    }
                },
                onShare = {
                    val formattedText = htmlToWhatsApp(devocionalText)
                    val textToShare = "A.R.D.E.: ${arde.reference}\n\n$formattedText"
                    if (!shareText(textToShare)) {
                        copyToClipboard(textToShare)
                        showFeedback("Copiado al portapapeles")
                    }
                },
                onClose = onClose
            )

            Div(attrs = { style { height(16.px) } })
            
            if (arde.reference.isNotEmpty()) {
                BibleLinkCard(
                    ardeReference = arde.reference,
                    showFeedback = showFeedback
                )
            }
            
            Div(attrs = { style { height(64.px) } }) // Margen inferior extra para que el scroll no quede tapado por el Toast
        }

        FeedbackToast(message = feedbackMessage)
    }
}

@Composable
private fun DevocionalHeader(reference: String) {
    H2(attrs = {
        style {
            color(White)
            margin(0.px)
            fontSize(24.px)
        }
    }) {
        Text("A.R.D.E.: $reference")
    }
}

@Composable
private fun DevocionalEditor(value: String, onValueChange: (String) -> Unit) {
    var activeFormats by remember { mutableStateOf(setOf<String>()) }
    val checkFormats: () -> Unit = {
        val doc = window.document.asDynamic()
        val newFormats = mutableSetOf<String>()
        if (doc.queryCommandState("bold") as Boolean) newFormats.add("bold")
        if (doc.queryCommandState("italic") as Boolean) newFormats.add("italic")
        if (doc.queryCommandState("insertUnorderedList") as Boolean) newFormats.add("insertUnorderedList")
        activeFormats = newFormats
    }

    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
        // Hack para inyectar CSS del placeholder de contenteditable
        Div(attrs = { 
            ref { 
                it.innerHTML = "<style>#devocional-content-area[contenteditable]:empty::before { content: attr(placeholder); color: rgba(255, 255, 255, 0.5); pointer-events: none; display: block; }</style>" 
                onDispose {} 
            }
            style { display(DisplayStyle.None) }
        })

        // Barra de herramientas (Toolbar)
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); gap(12.px); padding(8.px, 12.px)
                backgroundColor(VspMarcoTransparente50); borderRadius(12.px)
                marginBottom(4.px); alignItems(AlignItems.Center)
            }
        }) {
            val btnStyle: (Boolean) -> StyleScope.() -> Unit = { isActive -> { 
                backgroundColor(if (isActive) rgba(255, 255, 255, 0.2) else Color("transparent")); color(VspMarco); border(0.px)
                borderRadius(6.px); cursor("pointer"); fontSize(18.px); fontWeight("bold"); padding(4.px, 8.px)
            } }
            Button(attrs = { onClick { aplicarFormatoDevocional("bold") { onValueChange(it); checkFormats() } }; style { btnStyle(activeFormats.contains("bold"))(this) }; title("Negrita (WhatsApp: *texto*)") }) { Text("B") }
            Button(attrs = { onClick { aplicarFormatoDevocional("italic") { onValueChange(it); checkFormats() } }; style { btnStyle(activeFormats.contains("italic"))(this) }; title("Cursiva (WhatsApp: _texto_)") }) { Text("I") }
            Button(attrs = { onClick { aplicarFormatoDevocional("insertUnorderedList") { onValueChange(it); checkFormats() } }; style { btnStyle(activeFormats.contains("insertUnorderedList"))(this) }; title("Lista") }) { Text("•") }
            Span(attrs = { style { color(rgba(255,255,255,0.4)); fontSize(12.px); marginLeft(8.px) } }) {
                Text("Formato (Compatible con WhatsApp)")
            }
        }

        // Área de Texto (contenteditable) WYSIWYG
        Div(
            attrs = {
                id("devocional-content-area")
                attr("contenteditable", "true")
                attr("placeholder", "Escribe Tu ARDE de hoy...")
                addEventListener("input") { event ->
                    val target = event.nativeEvent.target.asDynamic()
                    onValueChange(target.innerHTML as String)
                    checkFormats()
                }
                addEventListener("keyup") { checkFormats() }
                addEventListener("mouseup") { checkFormats() }
                style {
                    width(100.percent); minHeight(200.px)
                    backgroundColor(VspMarcoTransparente50); color(White)
                    border(0.px); borderRadius(12.px); padding(16.px)
                    fontSize(16.px); fontFamily("sans-serif"); boxSizing("border-box")
                    property("resize", "vertical"); outline("none"); property("overflow-y", "auto")
                }
            }
        )

        LaunchedEffect(value) {
            val element = window.document.getElementById("devocional-content-area").asDynamic()
            if (element != null && element.innerHTML != value) {
                var safeHtml = value
                // Auto-conversión de markdown antiguo a HTML
                if (safeHtml.contains("**") || (safeHtml.contains("*") && !safeHtml.contains("<"))) {
                    safeHtml = safeHtml.replace(Regex("\\*\\*(.*?)\\*\\*"), "<b>$1</b>")
                    safeHtml = safeHtml.replace(Regex("\\*(.*?)\\*"), "<i>$1</i>")
                    safeHtml = safeHtml.replace("\n", "<br>")
                }
                element.innerHTML = safeHtml
            }
        }
    }
}

@Composable
private fun DevocionalActions(onSave: () -> Unit, onShare: () -> Unit, onClose: () -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
        }
    }) {
        Div(attrs = {
            style { display(DisplayStyle.Flex); gap(8.px); width(100.percent) }
        }) {
            Button(attrs = {
                onClick { onSave() }
                style { flexGrow(1); padding(12.px); backgroundColor(VspMarco); color(White); fontWeight("bold"); border(0.px); borderRadius(12.px); cursor("pointer"); fontSize(16.px) }
            }) { Text("Guardar") }
            
            Button(attrs = {
                onClick { onShare() }
                style { flexGrow(1); padding(12.px); backgroundColor(VspMarco); color(White); fontWeight("bold"); border(0.px); borderRadius(12.px); cursor("pointer"); fontSize(16.px) }
            }) { Text("Compartir") }
        }
        
        Button(attrs = {
            onClick { onClose() }
            style { width(100.percent); padding(12.px); backgroundColor(Color.darkgray); color(White); border(0.px); borderRadius(12.px); cursor("pointer"); fontSize(16.px) }
        }) { Text("Cerrar Devocional") }
    }
}

@Composable
private fun FeedbackToast(message: String) {
    Div(attrs = {
        style {
            position(Position.Fixed)
            bottom(24.px)
            left(50.percent)
            property("transform", "translateX(-50%)")
            backgroundColor(rgba(76, 175, 80, 0.2)) // Color success con alpha
            color(Color("#81C784"))
            padding(12.px, 24.px)
            borderRadius(8.px)
            fontWeight("bold")
            property("z-index", 1000)
            // Transición CSS para mostrar/ocultar suavemente
            property("transition", "opacity 0.3s ease, transform 0.3s ease")
            opacity(if (message.isNotEmpty()) 1 else 0)
            property("pointer-events", if (message.isNotEmpty()) "auto" else "none")
        }
    }) {
        Text(message)
    }
}

@Composable
fun BibleLinkCard(ardeReference: String, showFeedback: (String) -> Unit) {
    val bibleComAbbreviation = BibleUtils.convertReferenceToBibleComFormat(ardeReference)
    if (bibleComAbbreviation.isEmpty()) return

    val completeUrl = "https://www.bible.com/es/bible/103/$bibleComAbbreviation.NBLA"

    var chapterText by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var fontSize by remember { mutableStateOf(getFromLocalStorage(FONT_SIZE_KEY)?.toIntOrNull() ?: 16) }

    LaunchedEffect(ardeReference) {
        isLoading = true
        try {
            chapterText = BibleRepository.getChapterText(ardeReference)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            chapterText = null 
        } finally {
            isLoading = false
        }
    }

    BibleCardContainer(
        ardeReference = ardeReference,
        onOpenBibleCom = { window.open(completeUrl, "_blank") },
        onChangeFontSize = { delta ->
            val newSize = (fontSize + delta).coerceIn(12, 40)
            if (newSize != fontSize) {
                fontSize = newSize
                saveToLocalStorage(FONT_SIZE_KEY, newSize.toString())
            }
        }
    ) {
        when {
            isLoading -> {
                Div(attrs = { style { width(100.percent); height(200.px); display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center); color(VspMarco); fontWeight("bold") } }) {
                    Text("Cargando capítulo...")
                }
            }
            chapterText != null -> {
                BibleTextDisplay(
                    text = chapterText!!,
                    fontSize = fontSize,
                    onCopyText = {
                        copyToClipboard(chapterText!!)
                        showFeedback("¡Capítulo copiado al portapapeles!")
                    }
                )
            }
            else -> {
                P(attrs = { style { color(rgba(255, 255, 255, 0.7)); textAlign("center") } }) {
                    Text("No se pudo cargar el texto bíblico. Por favor, usa el botón superior.")
                }
            }
        }
    }
}

@Composable
private fun BibleCardContainer(
    ardeReference: String,
    onOpenBibleCom: () -> Unit,
    onChangeFontSize: (Int) -> Unit,
    content: @Composable () -> Unit
) {
    Div(attrs = {
        style { width(100.percent); backgroundColor(VspMarcoTransparente50); borderRadius(16.px); padding(24.px); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center) }
    }) {
        Button(attrs = {
            onClick { onOpenBibleCom() }
            style { padding(12.px, 24.px); backgroundColor(VspMarco); color(White); fontWeight("bold"); border(0.px); borderRadius(12.px); cursor("pointer"); fontSize(16.px) }
        }) { Text("Leer capítulo en Bible.com (NBLA)") }
        
        Div(attrs = { style { height(16.px) } })
        
        Div(attrs = { style { width(100.percent); display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center) } }) {
            Span(attrs = { style { color(rgba(255, 255, 255, 0.7)); fontSize(14.px) } }) { Text("Lectura de hoy") }
            
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
                Button(attrs = { onClick { onChangeFontSize(-2) }; style { backgroundColor(Color.transparent); color(White); border(0.px); fontWeight("bold"); fontSize(14.px); cursor("pointer") } }) { Text("A-") }
                Button(attrs = { onClick { onChangeFontSize(2) }; style { backgroundColor(Color.transparent); color(White); border(0.px); fontWeight("bold"); fontSize(18.px); cursor("pointer") } }) { Text("A+") }
            }
        }
        
        H3(attrs = { style { color(White); margin(4.px, 0.px); fontSize(20.px) } }) { Text(ardeReference) }
        Span(attrs = { style { color(rgba(255, 255, 255, 0.7)); fontSize(12.px) } }) { Text("Reina Valera (RV1960)") }
        
        Div(attrs = { style { height(16.px) } })

        content()
    }
}

@Composable
private fun BibleTextDisplay(text: String, fontSize: Int, onCopyText: () -> Unit) {
    // Ya no requerimos SelectionContainer, los Divs en web son seleccionables por defecto.
    Div(attrs = {
        style { color(White); fontSize(fontSize.px); lineHeight("1.6em"); textAlign("justify"); width(100.percent) }
    }) {
        // Utilizamos la función de Phase 1 que ya adaptamos para retornar elementos del DOM
        BibleUtils.FormatBibleText(rawText = text, baseFontSize = fontSize)
    }
    
    Div(attrs = { style { height(16.px) } })
    
    Button(attrs = {
        onClick { onCopyText() }
        style { padding(12.px); backgroundColor(VspMarcoTransparente50); color(White); border(0.px); borderRadius(12.px); cursor("pointer"); fontSize(16.px); width(100.percent) }
    }) { Text("📋 Copiar todo el capítulo") }

    Div(attrs = { style { height(16.px) } })
    P(attrs = { style { color(rgba(255, 255, 255, 0.5)); fontSize(12.px); textAlign("center"); margin(0.px) } }) {
        Text("Texto bíblico: Reina Valera (RV1960).")
        Br()
        Text("Para leer en NBLA usa el botón superior.")
    }
}